import { useQuery } from '@tanstack/react-query';
import { env } from '@/lib/env';

export interface SummarySource {
  id: number;
  title: string;
  url: string;
  source: string;
}

export interface TickerSummary {
  summary_markdown: string;
  sources: SummarySource[];
}

// In dev the Vite proxy forwards /api/summarise → localhost:8787 (no CORS).
// In production VITE_SUMMARISE_TICKER_URL points to the deployed worker URL.
const SUMMARISE_URL =
  import.meta.env.DEV ? '/api/summarise' : env.VITE_SUMMARISE_TICKER_URL;

async function fetchSummary(tickerId: number): Promise<TickerSummary> {
  const res = await fetch(SUMMARISE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticker_id: tickerId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<TickerSummary>;
}

export function useTickerSummary(tickerId: number | null) {
  return useQuery({
    queryKey: ['ticker-summary', tickerId],
    queryFn: () => fetchSummary(tickerId!),
    enabled: tickerId !== null,
    staleTime: 5 * 60_000,
    gcTime: 60 * 60_000,
    retry: 1,
  });
}
